/**
 * ============================================================================
 * OBJECTS STORE - FETCH OPERATIONS
 * ============================================================================
 * 
 * Handles fetching objects from the backend API and syncing with local store.
 * 
 * Data Flow:
 * 1. Request objects from API with optional folder filter
 * 2. API returns array of ObjectItem
 * 3. Store syncs with fetched data
 * 4. Returns fetched items
 * 
 * @module objects/api/fetch
 */

import { apiService } from "@/lib/api-functions/api-service";
import { objectsStore } from "../store";
import type { ObjectItem, ToastFunction } from "../types";

// ============================================================================
// FETCH OPERATIONS
// ============================================================================

/**
 * Fetch objects for a specific game from the API and sync the store.
 * 
 * @param gameId - The game ID to fetch objects for
 * @param folder - Optional folder ID to filter items by folder
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to array of fetched objects
 * 
 * @example
 * ```ts
 * // Fetch all objects for a game
 * const objects = await fetchObjects(gameId);
 * 
 * // Fetch objects in a specific folder
 * const folderObjects = await fetchObjects(gameId, folderId);
 * ```
 */
export async function fetchObjects(
  gameId: string,
  folder?: string | null,
  toast?: ToastFunction
): Promise<ObjectItem[]> {
  try {
    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Fetch data from API
    const result = await apiService.fetchObjects(gameId, folder);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch objects');
    }

    const objects = result.data as unknown as ObjectItem[];
    
    // Sync the store with the fetched data
    await objectsStore.syncFromRemote(async () => objects);
    
    return objects;
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch objects';
    console.error(`[fetchObjects] Error for gameId: ${gameId}:`, err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
